import { Box } from "@primer/react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";

import { useEffect, useMemo, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import { useSelector } from "react-redux";
import { DashBoardCongNoModel } from "../../models/response/thu-phi/DashBoardCongNoModel";
import { RootState } from "../../state/reducers";
import { baoCaoHocPhiApi } from "../../api/baoCaoHocPhiApi";

// Đăng ký các thành phần cho biểu đồ
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ChartCongNoPieChart = () => {
  const [dashBoardModel, setDashBoardModel] = useState<DashBoardCongNoModel[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { dm_coso_id, nam_hoc } = useSelector(
    (state: RootState) => state.common
  );

  useEffect(() => {
    const handleReloadDashBoard = async () => {
      try {
        setIsLoading(true);
        const res = await baoCaoHocPhiApi.SelectDashBoard({
          dm_coso_id,
          nam_hoc,
        });
        if (res.is_success) {
          setDashBoardModel(res.data);
        }
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    handleReloadDashBoard();
  }, [dm_coso_id, nam_hoc]);

  // Tính tổng công nợ cho biểu đồ tròn
  const congNoData = useMemo(() => {
    const totals = dashBoardModel.reduce(
      (acc, item) => {
        acc.tong_tien_phai_nop_all += item.tong_tien_phai_nop_all;
        acc.tong_tien_mien_giam_all += item.tong_tien_mien_giam_all;
        acc.tong_con_lai_phai_dong += item.tong_con_lai_phai_dong;
        acc.tong_thuc_nop += item.tong_thuc_nop;
        return acc;
      },
      {
        tong_tien_phai_nop_all: 0,
        tong_tien_mien_giam_all: 0,
        tong_con_lai_phai_dong: 0,
        tong_thuc_nop: 0,
      }
    );

    return totals;
  }, [dashBoardModel]);

  // Dữ liệu cho biểu đồ cột - 4 thông tin công nợ
  const barDataSource = useMemo(() => {
    return [
      {
        label: "Định mức",
        value: congNoData.tong_tien_phai_nop_all,
        color: "#5372F3", // Xám nhạt - Tổng định mức
      },
      {
        label: "Miễn giảm",
        value: congNoData.tong_tien_mien_giam_all,
        color: "#4A90E2", // Xanh dương - Miễn giảm
      },
      {
        label: "Phải nộp",
        value: congNoData.tong_con_lai_phai_dong,
        color: "#E57D7E", // Đỏ - Phải nộp
      },
      {
        label: "Đã nộp",
        value: congNoData.tong_thuc_nop,
        color: "#27AE60", // Xanh lá - Đã nộp
      },
    ];
  }, [congNoData]);

  // Format số tiền thành VND
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Kiểm tra tính hợp lệ của dữ liệu cho pie chart (chỉ phải nộp và đã nộp)
  const isDataValid = useMemo(() => {
    const total = congNoData.tong_con_lai_phai_dong + congNoData.tong_thuc_nop;
    const hasValidData = total > 0;

    console.log("Data Validation:", {
      hasValidData,
      total,
      phai_nop: congNoData.tong_con_lai_phai_dong,
      da_nop: congNoData.tong_thuc_nop,
    });

    return hasValidData;
  }, [congNoData]);

  // Chuẩn bị dữ liệu cho Bar Chart
  const barData = {
    labels: barDataSource.map((item) => item.label),
    datasets: [
      {
        label: "Số tiền (VND)",
        data: barDataSource.map((item) => item.value),
        backgroundColor: barDataSource.map((item) => item.color),
        borderColor: barDataSource.map((item) => item.color),
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  // Cấu hình options cho Bar Chart
  const barOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        display: true,
        labels: {
          font: {
            size: 12,
            weight: "bold",
          },
          color: "#182D62",
        },
      },
      title: {
        display: true,
        text: "Phân tích công nợ chi tiết",
        font: {
          size: 14,
          weight: "bold",
        },
        color: "#182D62",
        padding: 15,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#182D62",
        borderWidth: 1,
        callbacks: {
          label: function (context: any) {
            return `${context.dataset.label}: ${formatCurrency(
              context.parsed.y
            )}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            size: 10,
          },
          color: "#666",
          callback: function (value: any) {
            return formatCurrency(value);
          },
        },
        title: {
          display: true,
          text: "Số tiền (VND)",
          font: {
            size: 12,
            weight: "bold",
          },
          color: "#182D62",
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
            weight: "bold",
          },
          color: "#182D62",
        },
      },
    },
    animation: {
      duration: 1000,
      easing: "easeInOutQuart",
    },
  };

  // Chuẩn bị dữ liệu cho Pie Chart công nợ - hiển thị 2 phần: phải nộp và đã nộp
  const pieData = {
    labels: ["Phải nộp", "Đã nộp"],
    datasets: [
      {
        data: [congNoData.tong_con_lai_phai_dong, congNoData.tong_thuc_nop],
        backgroundColor: [
          "#E57D7E", // Đỏ - Phải nộp
          "#27AE60", // Xanh lá - Đã nộp
        ],
        borderColor: ["#E57D7E", "#27AE60"],
        borderWidth: 2,
        hoverOffset: 4,
      },
    ],
  };

  // Cấu hình options cho Pie Chart
  const pieOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        display: true,
        labels: {
          font: {
            size: 12,
            weight: "bold",
          },
          color: "#182D62",
          padding: 15,
          usePointStyle: true,
        },
      },
      title: {
        display: true,
        text: "Tình trạng thanh toán",
        font: {
          size: 16,
          weight: "bold",
        },
        color: "#182D62",
        padding: 20,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#182D62",
        borderWidth: 1,
        callbacks: {
          label: function (context: any) {
            const total = context.dataset.data.reduce(
              (a: number, b: number) => a + b,
              0
            );
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${formatCurrency(
              context.parsed
            )} (${percentage}%)`;
          },
        },
      },
    },
    animation: {
      duration: 1000,
      easing: "easeInOutQuart",
    },
  };

  // Component Loading
  const LoadingComponent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "400px",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #006bff",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          "@keyframes spin": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
          },
        }}
      />
      <Box
        sx={{
          fontSize: "1rem",
          color: "#666",
          fontWeight: 500,
        }}
      >
        Đang tải dữ liệu...
      </Box>
    </Box>
  );

  // Nếu đang loading, hiển thị loading component
  if (isLoading) {
    return (
      <Box
        sx={{
          padding: 3,
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          height: "100%",
        }}
      >
        <LoadingComponent />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: "#fff",
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        height: "100%",
      }}
    >
      {/* Header thông tin tổng quan */}
      <Box
        sx={{
          marginBottom: 3,
          textAlign: "center",
          borderBottom: "1px solid #ddd",
          paddingBottom: 2,
        }}
      >
        <Box
          sx={{
            fontSize: 18,
            fontWeight: 600,
            color: "#666",
            marginBottom: 1,
          }}
        >
          Tổng quan Công nợ
        </Box>
        {dashBoardModel.length > 0 && (
          <Box
            sx={{
              fontSize: "0.9rem",
              color: "#666",
              marginTop: 1,
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <span>
              Định mức: {formatCurrency(congNoData.tong_tien_phai_nop_all)}
            </span>
            <span>|</span>
            <span style={{ color: "#B8D4F0", fontWeight: "bold" }}>
              Miễn giảm: {formatCurrency(congNoData.tong_tien_mien_giam_all)}
            </span>
            <span>|</span>
            <span style={{ color: "#F8B4B4", fontWeight: "bold" }}>
              Phải nộp: {formatCurrency(congNoData.tong_con_lai_phai_dong)}
            </span>
            <span>|</span>
            <span style={{ color: "#A8E6CF", fontWeight: "bold" }}>
              Đã nộp: {formatCurrency(congNoData.tong_thuc_nop)}
            </span>
          </Box>
        )}
      </Box>

      {/* Layout split-screen cho 2 biểu đồ */}
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: "1fr 1fr", // Chia đôi màn hình
          height: 400,
          "@media (max-width: 768px)": {
            gridTemplateColumns: "1fr", // Mobile: 1 cột
            height: "auto",
          },
        }}
      >
        {/* Biểu đồ cột - Bên trái */}
        <Box
          sx={{
            position: "relative",
            "@media (max-width: 768px)": {
              height: 300,
            },
          }}
        >
          {barDataSource.length > 0 && isDataValid ? (
            <Bar data={barData} options={barOptions} />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                fontSize: "1rem",
                color: "#666",
                fontStyle: "italic",
                textAlign: "center",
              }}
            >
              {barDataSource.length === 0 ? (
                <> Không có dữ liệu</>
              ) : (
                <>⚠️ Dữ liệu không hợp lệ</>
              )}
            </Box>
          )}
        </Box>

        {/* Biểu đồ tròn - Bên phải */}
        <Box
          sx={{
            position: "relative",
            "@media (max-width: 768px)": {
              height: 300,
            },
          }}
        >
          {dashBoardModel.length > 0 && isDataValid ? (
            <Pie data={pieData} options={pieOptions} />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                fontSize: "1rem",
                color: "#666",
                fontStyle: "italic",
                textAlign: "center",
              }}
            >
              {dashBoardModel.length === 0 ? (
                <>Không có dữ liệu biểu</>
              ) : (
                <>⚠️ Dữ liệu không hợp lệ</>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChartCongNoPieChart;
